import { getMomentTz } from "@dexteel/mesf-core";
import { CenteredLazyLoading } from "@/controls/LazyLoading";

import {
	routeCalendarYear,
	routeCalendarYearM1,
	routeCalendarYearM2,
} from "@/setup/routes/calendar-year.routes";
import {
	routeCalendar,
	routeCalendarM1,
	routeCalendarM2,
} from "@/setup/routes/calendar.routes";
import { billetStatus } from "@/shared/selectors/BilletStatusSelector";
import { eventTypes } from "@/shared/selectors/EventTypeSelector";
import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import { masterDetailsMinHeight, pageBgColor } from "@/utils/theme";
import { useCalendarAreaId } from "../calendar/context/CalendarAreaContext";
import { MILL_1, MILL_2 } from "@/setup/mill-config/mill-config";
import {
	alpha,
	Box,
	Button,
	ButtonGroup,
	Checkbox,
	FormControlLabel,
	Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
	useLocation,
	useNavigate,
	useParams,
	useSearchParams,
} from "react-router-dom";
import { EventModal } from "../calendar/components/modals/EventModal";
import { CalendarYearTable } from "./CalendarYearTable";
import { EventYear } from "./models/EventYear";
import { getCalendarYear } from "./repositories/CalendarYearRepository";
import { buildYearGrid } from "./utils/yearGrid";

const moment = getMomentTz();
export const useSearchYearEvents = ({
	year,
	isDraftMode = false,
	onlyProduction = true,
}: {
	year: number;
	isDraftMode?: boolean;
	onlyProduction?: boolean;
}) => {
	const defaultAreaId = useCalendarAreaId();

	return useQuery<EventYear[], Error>({
		queryKey: [
			"calendar_year",
			year,
			isDraftMode,
			onlyProduction,
			defaultAreaId,
		],
		queryFn: ({ signal }) =>
			getCalendarYear(
				{ year, isDraftMode, onlyProduction, defaultAreaId },
				signal,
			),
		refetchInterval: 60_000,
	});
};

export const CalendarYearPage = () => {
	const navigate = useNavigate();
	const { year: yearParam } = useParams();
	const [searchParams, setSearchParams] = useSearchParams();
	const areaId = useCalendarAreaId();
	const millLabel =
		Number(areaId) === MILL_1.id
			? "M1"
			: Number(areaId) === MILL_2.id
				? "M2"
				: null;
	const location = useLocation();
	const yearBase = location.pathname.startsWith(routeCalendarYearM1)
		? routeCalendarYearM1
		: location.pathname.startsWith(routeCalendarYearM2)
			? routeCalendarYearM2
			: routeCalendarYear;
	const calendarBase =
		yearBase === routeCalendarYearM1
			? routeCalendarM1
			: yearBase === routeCalendarYearM2
				? routeCalendarM2
				: routeCalendar;

	const currentYear = new Date().getFullYear();
	const parsedYear = Number(yearParam);
	const year =
		Number.isInteger(parsedYear) && parsedYear > 0 ? parsedYear : currentYear;
	const onlyProduction = searchParams.get("onlyProductionEvents") !== "false";

	const setYear = (newYear: number) => {
		const querySearch = searchParams.toString();
		navigate(`${yearBase}/${newYear}${querySearch ? `?${querySearch}` : ""}`);
	};

	const setOnlyProduction = (value: boolean) => {
		const newParams = new URLSearchParams(searchParams);
		newParams.set("onlyProductionEvents", String(value));
		setSearchParams(newParams, { replace: true });
	};

	const [isDraftMode, setIsDraftMode] = useState(false);
	const [showByBilletStatus, setShowByBilletStatus] = useState(true);
	const [error, setError] = useState("");
	const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
	const [selectedEventMonthStart, setSelectedEventMonthStart] =
		useState<Date | null>(null);

	const {
		data,
		isLoading,
		error: e,
		isError,
	} = useSearchYearEvents({
		year,
		isDraftMode,
		onlyProduction,
	});

	const grid = buildYearGrid(data ?? [], year);

	const families = () => {
		const map = new Map<number, { name: string; color: string | null }>();
		(data ?? []).forEach((e) => {
			if (e.SectionFamilyId != null && !map.has(e.SectionFamilyId)) {
				map.set(e.SectionFamilyId, {
					name: e.SectionFamilyName ?? `Family ${e.SectionFamilyId}`,
					color: e.SectionFamilyColor ?? null,
				});
			}
		});
		return Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
	};

	const eventTypeLegends = () => {
		const nonProductionEvents = new Set<string>();
		(data ?? []).forEach((e) => {
			if (e?.EventTypeCode !== "P") nonProductionEvents.add(e.EventTypeCode);
		});
		return eventTypes.filter((t) => nonProductionEvents.has(t.code));
	};

	const goToMonth = (monthIndex: number, eventId?: number) => {
		const dateStr = `${year}-${String(monthIndex + 1).padStart(2, "0")}-01`;
		const mode = isDraftMode ? "draft" : "published";
		const params = new URLSearchParams();
		params.set("view", "list");
		params.set("date", dateStr);
		params.set("mode", mode);
		if (eventId != null) params.set("eventId", String(eventId));
		window.open(`${calendarBase}?${params.toString()}`, "_blank");
	};

	useEffect(() => {
		const noYear = !yearParam || parsedYear <= 0;
		const noSearchParams = !searchParams.has("onlyProductionEvents");

		if (!noYear && !noSearchParams) return;

		const newParams = new URLSearchParams(searchParams);
		if (noSearchParams) {
			newParams.set("onlyProductionEvents", "true");
		}

		if (noYear) {
			const querySearch = newParams.toString();
			navigate(
				`${yearBase}/${currentYear}${querySearch ? `?${querySearch}` : ""}`,
				{
					replace: true,
				},
			);
		} else {
			setSearchParams(newParams, { replace: true });
		}
	}, [yearParam, parsedYear, currentYear, searchParams]);

	useEffect(() => {
		if (isError && e) {
			setError(e.message);
		}
	}, [isError, e]);

	return (
		<>
			<HelmetDexteel title={`Year ${year} - Calendar`} />
			<Box
				sx={{
					padding: 2,
					minHeight: masterDetailsMinHeight,
					backgroundColor: (theme) =>
						isDraftMode ? alpha(theme.palette.warning.main, 0.2) : pageBgColor,
					transition: "background-color 0.3s ease-in-out",
				}}
			>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						mb: 2,
					}}
				>
					<ButtonGroup>
						<Button variant="outlined" onClick={() => setYear(year - 1)}>
							Previous
						</Button>
						<Button
							variant={year === currentYear ? "contained" : "outlined"}
							onClick={() => setYear(currentYear)}
							color="primary"
						>
							{currentYear}
						</Button>
						<Button variant="outlined" onClick={() => setYear(year + 1)}>
							Next
						</Button>
					</ButtonGroup>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
						}}
					>
						{millLabel && (
							<Typography
								variant="caption"
								sx={{
									color: "#666",
									fontWeight: 500,
									lineHeight: 1,
									letterSpacing: 0.5,
								}}
							>
								Year View {millLabel}
							</Typography>
						)}
						<Typography variant="h5" fontWeight={600}>
							{year}
						</Typography>
					</Box>
					<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
						<FormControlLabel
							control={
								<Checkbox
									size="small"
									checked={onlyProduction}
									onChange={(_, checked) => setOnlyProduction(checked)}
								/>
							}
							label="Only production events"
						/>
						{/* <ButtonGroup>
							<Button
								variant={isDraftMode ? "contained" : "outlined"}
								onClick={() => setIsDraftMode(true)}
								color="primary"
							>
								Draft
							</Button>
							<Button
								variant={isDraftMode ? "outlined" : "contained"}
								onClick={() => setIsDraftMode(false)}
								color="primary"
							>
								Published
							</Button>
						</ButtonGroup> */}
					</Box>
				</Box>

				{isLoading ? (
					<CenteredLazyLoading />
				) : (
					<CalendarYearTable
						year={year}
						grid={grid}
						goToMonth={goToMonth}
						onEventClick={(eventId) => {
							const event = (data ?? []).find((e) => e.EventId === eventId);
							setSelectedEventId(eventId);
							setSelectedEventMonthStart(
								event ? moment(event.Start).startOf("month").toDate() : null,
							);
						}}
						billetStatusMode={showByBilletStatus}
					/>
				)}

				{(families().length > 0 ||
					eventTypeLegends().length > 0 ||
					showByBilletStatus) && (
					<Box
						sx={{
							display: "flex",
							flexWrap: "wrap",
							gap: 1,
							mt: 2,
							justifyContent: "space-between",
							alignItems: "center",
						}}
					>
						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
							{showByBilletStatus
								? billetStatus.map((type) => (
										<Box
											key={`bs-${type.code}`}
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 0.5,
												px: 1,
												py: 0.5,
												borderRadius: 1,
												backgroundColor: "#fafafa",
											}}
										>
											<Box
												sx={{
													width: 14,
													height: 14,
													borderRadius: 0.5,
													backgroundColor: type.backgroundColor,
												}}
											/>
											<Typography variant="caption">
												Billets {type.name}
											</Typography>
										</Box>
									))
								: families().map(([id, { name, color }]) => (
										<Box
											key={`fam-${id}`}
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 0.5,
												px: 1,
												py: 0.5,
												borderRadius: 1,
												backgroundColor: "#fafafa",
											}}
										>
											<Box
												sx={{
													width: 14,
													height: 14,
													borderRadius: 0.5,
													backgroundColor: color,
												}}
											/>
											<Typography variant="caption">{name}</Typography>
										</Box>
									))}
							{eventTypeLegends().map((t) => (
								<Box
									key={`evt-${t.code}`}
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 0.5,
										px: 1,
										py: 0.5,
										borderRadius: 1,
										backgroundColor: "#fafafa",
									}}
								>
									<Box
										sx={{
											width: 14,
											height: 14,
											borderRadius: 0.5,
											backgroundColor: t.backgroundColor,
											border: `1px solid ${t.borderColor}`,
										}}
									/>
									<Typography variant="caption">{t.name}</Typography>
								</Box>
							))}
						</Box>
						<FormControlLabel
							control={
								<Checkbox
									size="small"
									checked={showByBilletStatus}
									onChange={(_, checked) => setShowByBilletStatus(checked)}
								/>
							}
							label="Show by Billet Status"
						/>
					</Box>
				)}
			</Box>
			<EventModal
				open={selectedEventId !== null}
				selectedEventId={selectedEventId ?? null}
				monthStart={selectedEventMonthStart}
				onHide={() => {
					setSelectedEventId(null);
					setSelectedEventMonthStart(null);
				}}
				isDraftMode={isDraftMode}
				disabled
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
